import type { PmmSession } from '../domain/auth'
import type { CandidatePublicationRequest, CandidateReviewRequest, CandidateRevisionRequest, CoachCandidateBundle, CoachProgressMemoryView, CoachProgressRunProjection, CoachReadinessPacket, RelationshipRunProjection } from '../domain/coach'
import type { ConsentDecisionRequest, ConsentState } from '../domain/consent'
import type { ClientAssetProjection } from '../domain/types'
import type { DailyExperienceRequest, DailyInteractionRequest, DailyInteractionResult, DailySwapRequest } from '../domain/daily'
import type { AssetResponse, DailyPlan } from '../domain/dailyEngine'
import type { FoundationCapacity, FoundationInteractionRequest, FoundationInteractionResult, FoundationView } from '../domain/foundation'
import type { ProfileFactCorrectionRequest, RelationshipIntelligenceBundle, SpineHypothesisReviewRequest } from '../domain/intelligence'
import type { DevicePrivacySettings, PrivacyJob, PrivacySnapshot } from '../domain/privacy'
import type { PracticeLibraryView, PracticePreferenceRequest, SaveWeeklyCouncilRequest, SaveWeeklyCouncilResult, WeeklyCouncilView } from '../domain/weekly'
import type { NotificationSettingsUpdate, NotificationSettingsView, NotificationTestResult, PushInstallationRegistration } from '../domain/notifications'
import type { OfflineSyncReport } from '../domain/offline'
import type {
  OnboardingChapterSaveRequest,
  OnboardingRestartRequest,
  OnboardingRestartResult,
  OnboardingSession,
  OnboardingSubmissionResult,
} from '../domain/onboarding'
import { FirestorePmmRepository } from './firestoreRepository'
import { prototypeRepository } from './prototypeRepository'

export interface PmmRepository {
  readonly mode: 'prototype' | 'firestore'
  getDailyExperience(request: DailyExperienceRequest): Promise<DailyPlan>
  regenerateDailyAsset(request: DailySwapRequest): Promise<DailyPlan>
  recordDailyInteraction(request: DailyInteractionRequest): Promise<DailyInteractionResult>
  getPracticeLibrary(): Promise<PracticeLibraryView>
  updatePracticePreference(request: PracticePreferenceRequest): Promise<PracticeLibraryView>
  getWeeklyCouncil(weekId: string): Promise<WeeklyCouncilView>
  saveWeeklyCouncil(request: SaveWeeklyCouncilRequest): Promise<SaveWeeklyCouncilResult>
  getFoundationJourney(capacity: FoundationCapacity): Promise<FoundationView>
  recordFoundationInteraction(request: FoundationInteractionRequest): Promise<FoundationInteractionResult>
  saveResponse(response: AssetResponse): Promise<void>
  publishClientAssets(assets: ClientAssetProjection[]): Promise<void>
  listPublishedClientAssets(): Promise<ClientAssetProjection[]>
  getCoachRelationshipReadiness(): Promise<CoachReadinessPacket>
  getCoachProgressMemory(): Promise<CoachProgressMemoryView>
  startCoachProgressMemory(clientMutationId: string): Promise<CoachProgressRunProjection>
  startRelationshipExpansion(clientMutationId: string, generationMode?: 'standard' | 'full_library_refresh'): Promise<RelationshipRunProjection>
  getRelationshipRunStatus(runId: string): Promise<RelationshipRunProjection>
  getCoachCandidateSet(candidateSetId: string): Promise<CoachCandidateBundle>
  saveCoachCandidateRevision(request: CandidateRevisionRequest): Promise<void>
  reviewCoachCandidate(request: CandidateReviewRequest): Promise<void>
  publishCoachCandidateSet(request: CandidatePublicationRequest): Promise<{ publishedCount: number }>
  getPrivacySnapshot(): Promise<PrivacySnapshot>
  acceptCoreService(): Promise<ConsentState>
  recordConsentDecision(input: ConsentDecisionRequest): Promise<ConsentState>
  updateDevicePrivacy(settings: Pick<DevicePrivacySettings, 'trustedDevice' | 'privacyMode'>): Promise<DevicePrivacySettings>
  requestExport(): Promise<PrivacyJob>
  requestDeletion(): Promise<PrivacyJob>
  clearSensitiveDeviceState(): Promise<void>
  syncOfflineQueue(): Promise<OfflineSyncReport>
  getNotificationSettings(): Promise<NotificationSettingsView>
  updateNotificationSettings(settings: NotificationSettingsUpdate): Promise<NotificationSettingsView>
  registerPushInstallation(input: PushInstallationRegistration): Promise<NotificationSettingsView>
  unregisterPushInstallation(installationId: string): Promise<NotificationSettingsView>
  sendTestNotification(): Promise<NotificationTestResult>
  getOnboardingSession(): Promise<OnboardingSession>
  saveOnboardingChapter(request: OnboardingChapterSaveRequest): Promise<OnboardingSession>
  submitOnboarding(sessionId: string, expectedRevision: number): Promise<OnboardingSubmissionResult>
  restartOnboardingTest(request: OnboardingRestartRequest): Promise<OnboardingRestartResult>
  getRelationshipIntelligence(): Promise<RelationshipIntelligenceBundle>
  correctProfileFact(request: ProfileFactCorrectionRequest): Promise<RelationshipIntelligenceBundle>
  reviewSpineHypothesis(request: SpineHypothesisReviewRequest): Promise<RelationshipIntelligenceBundle>
}

export function createPmmRepository(session: PmmSession): PmmRepository {
  return import.meta.env.VITE_PMM_DATA_MODE === 'emulator' || import.meta.env.VITE_PMM_DATA_MODE === 'firebase'
    ? new FirestorePmmRepository(session)
    : prototypeRepository
}
